import { groupBy } from 'lodash-es';
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form';

import { OrganizationRoleSchema, PermissionSchema } from '@/types/schema';

import { ReactHookFormRoleRequest } from '../types/organizationMembers.type';

const pluralRules = new Intl.PluralRules('en-US');

export const plural = (count: number, noun: string, suffix: string) => {
  return {
    one: `1 ${noun}`,
    two: `${count} ${noun}${suffix}`,
    few: `${count} ${noun}${suffix}`,
    other: `${count} ${noun}${suffix}`,
  }[pluralRules.select(count)];
};

export const sortRoles = (roles: OrganizationRoleSchema[] | undefined) => {
  if (!roles) return [];
  else {
    const sorted = [...roles];
    sorted.sort((a, b) => {
      if (a.name === 'CUSTOM') return 1;
      if (b.name === 'CUSTOM') return -1;
      return a.name > b.name ? 1 : -1;
    });
    return sorted;
  }
};

export const extractGroups = (
  permissions: PermissionSchema[] | undefined
): Record<string, PermissionSchema[]> => {
  if (!permissions) return {};
  return groupBy(
    permissions,
    (p) => p.name.split(':').at(0) as string
  ) as Record<string, PermissionSchema[]>;
};

export const mapPermissions = (
  permissions: PermissionSchema[] | undefined,
  accessControl: string,
  rolePermissions: string[]
): { name: string; description: string; access: boolean }[] => {
  if (!permissions) return [];

  return permissions
    .filter((permission) => permission.name.endsWith(accessControl))
    .map((permission) => ({
      name: permission.name.split(':').at(0) ?? '',
      description: permission.description,
      access: rolePermissions.includes(permission.name),
    }));
};

export const toggleAllOn = (
  schemaPerms: PermissionSchema[],
  existingGroupPerms: FieldArrayWithId<
    ReactHookFormRoleRequest,
    'permissions',
    'id'
  >[],
  append: UseFieldArrayAppend<ReactHookFormRoleRequest, 'permissions'>
) => {
  const permsToAdd = schemaPerms
    .map((p) => ({
      perm: p.name,
    }))
    .filter(
      (p) => !existingGroupPerms.find((existing) => p.perm === existing.perm)
    );
  append(permsToAdd);
};

export const toggleSingleOff = (
  schemaPerm: PermissionSchema,
  permissions: FieldArrayWithId<
    ReactHookFormRoleRequest,
    'permissions',
    'id'
  >[],
  remove: UseFieldArrayRemove
) => {
  const idx = permissions.findIndex(
    (existingPerm) =>
      existingPerm.perm === schemaPerm.name &&
      existingPerm.perm !== 'workspaces:read'
  );
  if (idx > -1) remove(idx);
};

export const toggleAllOff = (
  schemaPerms: PermissionSchema[],
  permissions: FieldArrayWithId<
    ReactHookFormRoleRequest,
    'permissions',
    'id'
  >[],
  remove: UseFieldArrayRemove
) => {
  const indicesToRemove = schemaPerms
    .map((p) => {
      return permissions.findIndex(
        (existingPerm) =>
          existingPerm.perm === p.name &&
          existingPerm.perm !== 'workspaces:read'
      );
    })
    .filter((i) => i !== -1);
  remove(indicesToRemove);
};

export const isSingleOn = (
  schemaPerm: PermissionSchema,
  existingGroupPerms: FieldArrayWithId<
    ReactHookFormRoleRequest,
    'permissions',
    'id'
  >[]
) => {
  return existingGroupPerms.some(
    (existing) => existing.perm === schemaPerm.name
  );
};

export const permToggleState = (
  schemaPerms: PermissionSchema[],
  existingGroupPerms: FieldArrayWithId<
    ReactHookFormRoleRequest,
    'permissions',
    'id'
  >[]
) => {
  return {
    checked: existingGroupPerms.length === schemaPerms.length,
    indeterminate:
      existingGroupPerms.length > 0 &&
      existingGroupPerms.length !== schemaPerms.length,
  };
};
